import styled from 'styled-components';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { BUTTON_SIZES, DestructiveButton } from 'ui/button';
import { caption, colors, spacing } from 'ui/theme';
import { SavedView } from 'modules/messagingInsights/types';
import { DeleteData, ListedView } from 'modules/savedViewsIndex/types';
import { DeleteViewsListContainer, DeleteViewsList, BoldSpan } from '../../../Styled';

const StyledModalBody = styled(ModalBody)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
`;

const CaptionText = styled.div`
  ${caption};
  color: ${colors.textSecondary};
`;

type Props = {
  viewsToBeDeleted: (SavedView | ListedView | DeleteData)[];
  deleteViews: () => void;
  onClose: () => void;
};

export const DeleteViewModal = ({ viewsToBeDeleted, deleteViews, onClose }: Props) => {
  const areMultipleViews = viewsToBeDeleted.length > 1;
  const viewsToBeDeletedText = areMultipleViews ? (
    <>
      these <BoldSpan>{viewsToBeDeleted.length} saved views</BoldSpan>
    </>
  ) : (
    <span>
      <BoldSpan>{viewsToBeDeleted[0].name}</BoldSpan>
    </span>
  );

  const deleteButtonText = areMultipleViews
    ? `Delete ${viewsToBeDeleted.length} views`
    : `Delete saved view`;
  const isPublic = areMultipleViews ? undefined : !viewsToBeDeleted[0].isPrivate;

  const handleClick = () => {
    deleteViews();
    onClose();
  };

  return (
    <Modal
      id="deleteModal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DELETE}
    >
      <ModalHeader>Delete saved view{areMultipleViews ? 's' : ''}?</ModalHeader>
      <StyledModalBody>
        <div>Are you sure you want to delete {viewsToBeDeletedText}?</div>
        {isPublic && (
          <div>
            Deleting this view will delete it for everyone in the project and cannot be undone.
          </div>
        )}
        {areMultipleViews && (
          <>
            <div>
              Deleting these views will delete them for everyone in the project and cannot be
              undone.
            </div>
            <DeleteViewsListContainer>
              <DeleteViewsList>
                {viewsToBeDeleted.map((view, i) => (
                  <li key={i}>{view.name}</li>
                ))}
              </DeleteViewsList>
              <CaptionText>Note: only views you have created can be deleted.</CaptionText>
            </DeleteViewsListContainer>
          </>
        )}
      </StyledModalBody>
      <ModalFooter>
        <DestructiveButton onClick={handleClick} size={BUTTON_SIZES.LARGE}>
          {deleteButtonText}
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  );
};
